import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import clsx from "clsx";
import { BASE_URL } from "../../config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

const InfoRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-2">
    <div className="w-1/3 min-w-[160px] text-sm font-medium text-gray-600">
      {label}
    </div>
    <div className="flex-1 text-sm text-gray-900 break-words">
      {value ?? "—"}
    </div>
  </div>
);

const SkeletonBlock = () => (
  <div className="animate-pulse">
    <div className="mb-4 h-6 w-1/3 rounded bg-gray-200" />
    <div className="space-y-2">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="h-4 w-full rounded bg-gray-100" />
      ))}
    </div>
  </div>
);

const BannerViewModal = ({ bannerId, onClose }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    const fetchBanner = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}/admin/banners/${bannerId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
          },
        });
        setFormData(response.data.banner);
      } catch (error) {
        toast.error("Failed to fetch banner details.");
        onClose();
      } finally {
        setIsLoading(false);
      }
    };
    fetchBanner();
  }, [bannerId]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="bg-white rounded-2xl p-6 sm:p-8 max-w-lg w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {isLoading ? (
            <SkeletonBlock />
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Banner Details</h3>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                  <FontAwesomeIcon icon={faTimes} className="h-6 w-6" />
                </button>
              </div>
              <InfoRow label="ID" value={formData?.id} />
              <InfoRow label="Title" value={formData?.title} />
              <InfoRow label="Message" value={formData?.message} />
              <InfoRow label="Start Time" value={new Date(formData?.start_time).toLocaleString()} />
              <InfoRow label="End Time" value={new Date(formData?.end_time).toLocaleString()} />
              <InfoRow label="Active" value={formData?.is_active ? "Yes" : "No"} />
              <InfoRow label="Created At" value={new Date(formData?.created_at).toLocaleString()} />
              <InfoRow label="Updated At" value={new Date(formData?.updated_at).toLocaleString()} />

              <div className="mt-6 flex justify-end gap-4">
                <button
                  type="button"
                  onClick={() => navigate(`/banners/edit/${bannerId}`)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-2"
                >
                  <PencilSquareIcon className="h-5 w-5" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BannerViewModal;